import {Component, Input, OnInit} from '@angular/core';
import {ConfirmationModalComponent, CopyLeagueModalComponent, ValidationModalComponent} from "@components";
import {
  AuthenticationService,
  ExportService,
  LeagueService,
  LoaderService,
  RequestService,
  ToastService
} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {ActivatedRoute, Router} from "@angular/router";
import {
  ADMINISTRATOR_ROLE,
  DEFAULT_PAGE_SIZE,
  EventItem,
  League,
  LeagueFilter,
  LeagueStatus,
  Lookups,
  Organiser, ORGANISERS_ADMINISTRATOR_ROLE,
  PagedFilter,
  Pages,
  Request,
  TOAST_STATE,
  ValidatorResult,
  VisibilityStatus
} from "@model";
import { environment } from "@environment";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {
  ADD, EDIT,
  MANAGEMENT,
} from "../../../../../app-routing.module";
import {PagingComponent} from "../../../../../components/paging.component";
import {debounceTime, distinctUntilChanged, Observable} from "rxjs";

@Component({
    selector: 'app-list-of-leagues-statements',
    templateUrl: './list-of-leagues.component.html',
    styleUrls: ['./list-of-leagues.component.scss'],
    standalone: false
})
export class ListOfLeaguesComponent extends PagingComponent<League> implements OnInit{
  @Input() leagues: League[] = [];
  @Input('leagueOrganisers') organisers: Organiser[] = [];
  @Input() leaguesStatuses: Lookups[] = [];
  @Input() visibilityStatuses: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() userRole: String | undefined;

  isAdministrator: boolean = false;

  name: string = "";
  leagueStatusId?: number;
  leagueVisibilityStatusId?: number;
  organiserId?: number;

  environment: any = environment;
  queryParamsFirstChange: boolean = true;


  constructor(private toastService: ToastService,
              private translocoService: TranslocoService, private router: Router,
              private leagueService: LeagueService, override loaderService: LoaderService,
              public authService: AuthenticationService, private modalService: NgbModal,
              private requestService: RequestService, private activatedRoute: ActivatedRoute,
              private exportService: ExportService
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.isAdministrator = this.userRole === ADMINISTRATOR_ROLE || this.userRole === ORGANISERS_ADMINISTRATOR_ROLE;
    this.leagueService.setCurrentLeague(undefined);
    this.handleExploreResult(this.leagues);
    this.subscribeToRouteDataChange();
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.activatedRoute.queryParams.pipe(
        debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()
    ).subscribe((queryParams: any) => {
      this.name = queryParams.name || "";
      this.organiserId = this.getOrganiserValue(queryParams);
      this.leagueStatusId = this.getStatusValue(queryParams);
      this.leagueVisibilityStatusId = this.getVisibilityStatusValue(queryParams);
      if (!this.queryParamsFirstChange && this.isLeagueStatusValid(queryParams.status) && this.isOrganiserValid(queryParams.organiser)) {
        this.onExplore(false);
      }
      this.queryParamsFirstChange = false;
    }));
  }


  private getStatusValue(queryParams: any) {
    return queryParams.status !== null && queryParams.status !== undefined && this.isLeagueStatusValid(queryParams.status) ? +queryParams.status : undefined;
  }
  private getVisibilityStatusValue(queryParams: any) {
    return queryParams.visibility !== null && queryParams.visibility !== undefined && this.isVisibilityStatusValid(queryParams.visibility) ? +queryParams.visibility : undefined;
  }

  private isLeagueStatusValid(statusId: string | undefined) {
    return !statusId || this.leaguesStatuses.some(status => status.id === +statusId);
  }
  private isVisibilityStatusValid(statusId: string | undefined) {
    return !statusId || this.visibilityStatuses.some(status => status.id === +statusId);
  }

  private getOrganiserValue(queryParams: any) {
    return queryParams.organiser && this.isOrganiserValid(queryParams.organiser) ? +queryParams.organiser : undefined;
  }

  private isOrganiserValid(organiserId: string | undefined) {
    return !organiserId || this.organisers.some(organiser => organiser.id === +organiserId);
  }


  protected fetchPage(page: number): Observable<League[]> {
    return this.leagueService.findLeagues(this.createFilter());
  }

  protected override afterSetItems(items: League[]): void {
    this.leagues = items;
  }

  protected override afterOnExplore(): void {
    this.scrollToTop();
  }



  trackByFn(index: number, item: EventItem): number | undefined {
    return item.id;
  }

  goToLeagueDetails(league: League): void {
    if (league && league.id) {
      this.router.navigate([EDIT], {queryParams: {leagueId: league.id}, relativeTo: this.activatedRoute});
    }
  }

  openDeleteLeagueDialog(league: League): void {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("leagues.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("leagues.delete.dialog.text");

    modalRef.result.then((_) => {
      this.deleteLeague(league);
    }, (_) => {
      /// ignore
    });
  }

  downloadLeagues(type: string) {
    this.exportService.downloadLeagues(type);
  }

  deleteLeague(league: League): void {
    this.loaderService.startLoading();
    this.leagueService.deleteLeague(league, () => {
      this.page = 1;
      this.leagueService.findLeagues(this.createFilter()).subscribe((leagues: League[]): void => {
        this.leagues = leagues;
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("leagues.delete.success"));
      });
    }, () => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("leagues.delete.error"));
    });
  }

  goToAddLeague(): void {
    this.router.navigate([ADD], {relativeTo: this.activatedRoute});
  }


  openPublishLeagueDialog(league: League) {
    if (this.authService.isOrganiser()) {
      this.sendPublishRequest(league);
    } else {
      this.validateLeague(league);
    }
  }


  onLeagueStatusChange(statusId: number | undefined) {
    this.leagueStatusId = statusId;
    this.changeQueryParams();
  }

  onLeagueVisibilityStatusChange(statusId: number | undefined) {
    this.leagueVisibilityStatusId = statusId;
    this.changeQueryParams();
  }

  onOrganiserChange(organiserId: number | undefined) {
    this.organiserId = organiserId;
    this.changeQueryParams();
  }

  handleSearchInput(value: string | Event): void {
    this.name = typeof value === 'string' ? value.trim() : (value.target as HTMLInputElement).value.trim();
    this.changeQueryParams();
  }

  private changeQueryParams() {
    const queryParams: { [key: string]: any } = {};
    if (this.leagueStatusId != null) queryParams['status'] = this.leagueStatusId;
    if (this.organiserId) queryParams['organiser'] = this.organiserId;
    if (this.name) queryParams['name'] = this.name;
    if (this.leagueVisibilityStatusId != null) queryParams['visibility'] = this.leagueVisibilityStatusId;
    this.router.navigate([], { relativeTo: this.activatedRoute, queryParams, replaceUrl: true });
  }

  openPublishLeagueAdminDialog(league: League) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.title = this.translocoService.translate("leagues.publish.modal.title");
    modalRef.componentInstance.text = this.translocoService.translate("leagues.publish.modal.text");
    modalRef.result.then(() => this.publishLeague(league), () => { });
  }

  publishLeague(league: League) {
    if (!league) return;
    this.leagueService.publishLeague(league!, () => {
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("league.publish.success"));
      league.visibilityStatus = VisibilityStatus.VISIBLE;
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("league.publish.error"));
    });
  }

  openHideLeagueDialog(league: League) {
    const isAdministrator = this.authService.isAdministrator();

    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate('league.details.hide');
    modalRef.componentInstance.text = this.translocoService.translate(isAdministrator ?
        'league.details.hide.modal.admin.text' :
        'league.details.hide.modal.organiser.text');

    modalRef.result.then((_) => {
      this.hideLeague(league);
    }, (_) => {
      /// ignore
    });
  }


  hideLeague(league: League) {
    if (!league) return;
    this.leagueService.hideLeague(league, () => {
      league.visibilityStatus = VisibilityStatus.HIDDEN;
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.error"));
    });
  }


  sendPublishRequest(league: League) {
    this.requestService.sendPublishLeagueRequest(Request.createPublishLeagueRequest(league), () => {
      league.visibilityStatus = VisibilityStatus.REQUEST_SENT;
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("request.sending.success"));
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.error"));
    }).subscribe((val: ValidatorResult | null) => {
      if (val) {
        this.openValidationModalComponent(val, league);
      }
    });
  }

  validateLeague(league: League) {
    this.leagueService.validateLeague(league.id!).subscribe((val: ValidatorResult | null) => {
      if (val) {
        this.openValidationModalComponent(val, league);
      }
      else {
        this.openPublishLeagueAdminDialog(league);
      }
    });
  }

  openValidationModalComponent(validatorResult: ValidatorResult, league: League) {
    const isAdministrator = this.authService.isAdministrator();
    const modalRef: NgbModalRef = this.modalService.open(ValidationModalComponent, { centered: true, size: 'lg' });

    modalRef.componentInstance.title = this.translocoService.translate("leagues.publish.modal.title");
    modalRef.componentInstance.text = this.translocoService.translate("leagues.publish.modal.validation.invalid");
    modalRef.componentInstance.keyPrefix = "league.details."
    if (isAdministrator) {
      modalRef.componentInstance.showConfirmButton = true;
      modalRef.componentInstance.confirmationText = this.translocoService.translate("leagues.publish.modal.text")
    }
    modalRef.componentInstance.validatorResult = validatorResult;
    modalRef.result.then(() => {
      if (isAdministrator) {
        this.publishLeague(league);
      }
    }, () => { });
  }

  onRefreshFilters(): void {
    this.leagueStatusId = undefined;
    this.organiserId = undefined;
    this.name = '';
    this.changeQueryParams();
  }



  createFilter(): PagedFilter<LeagueFilter> {
    return new PagedFilter(new Pages(DEFAULT_PAGE_SIZE, this.page), {
      name: this.name || undefined,
      leagueStatuses: (this.leagueStatusId !== null && this.leagueStatusId !== undefined) ? [this.leagueStatusId] : undefined,
      visibilityStatuses: (this.leagueVisibilityStatusId !== null && this.leagueVisibilityStatusId !== undefined) ? [this.leagueVisibilityStatusId] : undefined,
      organiserId: this.isAdministrator ? this.organiserId : undefined,
    });
  }

  goToLeagueManagement(league: League): void {
    if (league?.id) {
      this.leagueService.setCurrentLeague(league);
      this.router.navigate([MANAGEMENT], { queryParams: { leagueId: league.id }, relativeTo: this.activatedRoute });
    }
  }

  goToCopyLeague(league: League): void {
    if (league?.id) {
      const modalRef: NgbModalRef = this.modalService.open(CopyLeagueModalComponent, {
            centered: true,
            size: 'lg'
      });
      modalRef.componentInstance.league = league;
      modalRef.componentInstance.onSave.subscribe((val: League) => {
        modalRef.close();
        const onSuccess = (result: League) => {
          this.page = 1;
          this.leagueService.findLeagues(this.createFilter()).subscribe((leagues: League[]): void => {
            this.leagues = leagues;
            this.loaderService.dismissLoading();
            this.toastService.showToast(TOAST_STATE.success,
                this.translocoService.translate("leagues.copy.success"));
          });
        }
        const onError = () => {
          this.toastService.showToast(TOAST_STATE.danger,
                this.translocoService.translate("leagues.copy.error"));

        }
        this.loaderService.startLoading();
        this.leagueService.copyLeague(league!.id!, val, onSuccess, onError)
      });

      }
  }

  protected readonly VisibilityStatus = VisibilityStatus;
  protected readonly LeagueStatus = LeagueStatus;

}
